<template>
  <div class="sales-dashboard min-h-screen">
    <LoadingSpinner :isLoading="isLoading" />

    <!-- Header Section -->
    <div class="sd-header">
      <div class="w-full px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="sd-header__logo">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
            <div>
              <h1 class="text-xl font-semibold text-secondary-800">{{ $t('framework_agreements.title') }}</h1>
              <p class="text-sm text-gray-500">{{ $t('framework_agreements.subtitle') }}</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <div class="relative">
              <input 
                v-model="searchQuery" 
                type="text" 
                :placeholder="$t('framework_agreements.search_customer')"
                class="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
              />
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <BaseIcon icon="search" class="text-gray-400" />
              </div>
            </div>
            <button v-if="user.role === 'admin'" class="sd-btn sd-btn--primary" @click.prevent="openCreateModal">
              <BaseIcon icon="plus" class="mr-1" />
              {{ $t('framework_agreements.create_new') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Table Section -->
    <div class="w-full px-4 sm:px-6 lg:px-8 py-6">
      <div class="bg-white rounded-lg shadow overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-white border-b border-gray-200">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('framework_agreements.agreement_number') }}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('framework_agreements.country') }}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('framework_agreements.customer') }}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('framework_agreements.expires') }}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('framework_agreements.potential_alarms') }}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('framework_agreements.initial_value') }}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('framework_agreements.potential_value') }}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('framework_agreements.framework_pdf') }}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('framework_agreements.price_supplement') }}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-if="filteredAgreements.length === 0">
                <td colspan="10" class="px-4 py-8 text-center text-gray-400">
                  {{ searchQuery ? $t('framework_agreements.no_results') : $t('framework_agreements.no_agreements') }}
                </td>
              </tr>
              <tr v-for="agreement in filteredAgreements" :key="agreement._id" class="hover:bg-gray-50 cursor-pointer" @click="openEditModal(agreement)">
                <td class="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-700">{{ agreement.agreement_number || '—' }}</td>
                <td class="px-4 py-3 whitespace-nowrap text-sm">
                  <span v-if="agreement.country === 'no'" title="Norge">🇳🇴</span>
                  <span v-else title="Sverige">🇸🇪</span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{{ agreement.customer_name }}</td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  <span :class="isExpired(agreement.agreement_expires) ? 'text-red-600 font-semibold' : ''">
                    {{ formatDate(agreement.agreement_expires) }}
                  </span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{{ agreement.potential_alarm_count }}</td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{{ formatCurrency(agreement.initial_business_value) }}</td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{{ formatCurrency(agreement.potential_business_value) }}</td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  <a v-if="agreement.framework_agreement_file" :href="agreement.framework_agreement_file" target="_blank" @click.stop class="text-primary-600 hover:text-primary-800 flex items-center" :title="agreement.framework_agreement_filename">
                    <BaseIcon icon="file-pdf" class="mr-1" />PDF
                  </a>
                  <span v-else class="text-gray-300">—</span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  <a v-if="agreement.price_supplement_file" :href="agreement.price_supplement_file" target="_blank" @click.stop class="text-primary-600 hover:text-primary-800 flex items-center" :title="agreement.price_supplement_filename">
                    <BaseIcon icon="paperclip" class="mr-1" />Fil
                  </a>
                  <span v-else class="text-gray-300">—</span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-right">
                  <button v-if="user.role === 'admin'" class="text-red-500 hover:text-red-700 ml-2" @click.stop="confirmDelete(agreement)" :title="$t('framework_agreements.delete')">
                    <BaseIcon icon="trash" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Create / Edit Modal -->
    <modal :name="'modal-framework-agreement'" :scrollable="true" :adaptive="true" height="auto" width="800px" @before-close="resetForm">
      <div class="p-6">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-lg font-semibold text-secondary-800">
            {{ isEditing ? (user.role === 'admin' ? $t('framework_agreements.edit') : $t('framework_agreements.view')) : $t('framework_agreements.create_new') }}
          </h2>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
            <BaseIcon icon="times" />
          </button>
        </div>

        <form @submit.prevent="saveAgreement" class="space-y-6">
          <!-- Customer Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('framework_agreements.customer') }} *</label>
            <div class="relative">
              <input 
                v-model="customerSearch" 
                @input="onCustomerSearchInput"
                @focus="showCustomerDropdown = true"
                @blur="hideCustomerDropdown"
                :disabled="isEditing && user.role !== 'admin'"
                type="text" 
                :placeholder="$t('framework_agreements.search_customer_placeholder')"
                class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                required
              />
              <div v-if="showCustomerDropdown && filteredCustomers.length > 0" class="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                <div 
                  v-for="customer in filteredCustomers" 
                  :key="customer._id" 
                  @mousedown.prevent="selectCustomer(customer)"
                  class="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                >
                  {{ customer.name }}
                </div>
              </div>
            </div>
          </div>

          <!-- Agreement Number (read-only, shown when editing) -->
          <div v-if="isEditing && form.agreement_number" class="bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
            <span class="text-xs font-medium text-blue-600">{{ $t('framework_agreements.agreement_number') }}:</span>
            <span class="ml-2 text-sm font-mono font-semibold text-blue-900">{{ form.agreement_number }}</span>
          </div>

          <!-- Country Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('framework_agreements.country') }} *</label>
            <div v-if="!isEditing" class="flex gap-4 mt-1">
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="radio" v-model="form.country" value="se" class="w-4 h-4 text-primary-600" />
                <span class="text-sm">🇸🇪 {{ $t('framework_agreements.country_se') }}</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="radio" v-model="form.country" value="no" class="w-4 h-4 text-primary-600" />
                <span class="text-sm">🇳🇴 {{ $t('framework_agreements.country_no') }}</span>
              </label>
            </div>
            <div v-else class="text-sm text-gray-700 py-1">
              <span v-if="form.country === 'no'">🇳🇴 {{ $t('framework_agreements.country_no') }}</span>
              <span v-else>🇸🇪 {{ $t('framework_agreements.country_se') }}</span>
            </div>
          </div>

          <!-- Framework Agreement PDF -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('framework_agreements.framework_pdf') }} (PDF)</label>
            <div v-if="form.framework_agreement_file && !newFrameworkFile" class="flex items-center gap-2 mb-2">
              <a :href="form.framework_agreement_file" target="_blank" class="text-primary-600 hover:text-primary-800 text-sm">
                <BaseIcon icon="file-pdf" class="mr-1" />{{ form.framework_agreement_filename }}
              </a>
              <button v-if="user.role === 'admin'" type="button" @click="removeFrameworkAgreement" class="text-red-500 text-xs hover:text-red-700">
                <BaseIcon icon="times" />
              </button>
            </div>
            <input v-if="!isEditing || user.role === 'admin'" type="file" ref="frameworkFileInput" @change="onFrameworkFileChange" accept=".pdf" class="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" />
          </div>

          <!-- Price Supplement (Tilläggsprisbilaga) -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('framework_agreements.price_supplement') }} (PDF/Excel/Word)</label>
            <div v-if="form.price_supplement_file && !newPriceSupplementFile" class="flex items-center gap-2 mb-2">
              <a :href="form.price_supplement_file" target="_blank" class="text-primary-600 hover:text-primary-800 text-sm">
                <BaseIcon icon="paperclip" class="mr-1" />{{ form.price_supplement_filename }}
              </a>
              <button v-if="user.role === 'admin'" type="button" @click="removePriceSupplement" class="text-red-500 text-xs hover:text-red-700">
                <BaseIcon icon="times" />
              </button>
            </div>
            <input v-if="!isEditing || user.role === 'admin'" type="file" ref="priceSupplementFileInput" @change="onPriceSupplementFileChange" accept=".pdf,.doc,.docx,.xls,.xlsx" class="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" />
          </div>

          <!-- Procured Products (dynamic) -->
          <div class="border-t pt-4">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">{{ $t('framework_agreements.procured_prices') }}</h3>

            <!-- Legacy display for old agreements that used hardcoded fields -->
            <div v-if="isEditing && hasLegacyPrices && form.procured_products.length === 0" class="mb-4 bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p class="text-xs text-yellow-700 mb-2">{{ $t('framework_agreements.legacy_prices_note') }}</p>
              <div class="grid grid-cols-3 gap-4">
                <div>
                  <label class="block text-xs text-gray-500 mb-1">{{ $t('framework_agreements.price_alarm') }}</label>
                  <input type="text" :value="formatCurrency(form.price_per_alarm)" disabled class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-50" />
                </div>
                <div>
                  <label class="block text-xs text-gray-500 mb-1">{{ $t('framework_agreements.price_display') }}</label>
                  <input type="text" :value="formatCurrency(form.price_per_display)" disabled class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-50" />
                </div>
                <div>
                  <label class="block text-xs text-gray-500 mb-1">{{ $t('framework_agreements.price_tag') }}</label>
                  <input type="text" :value="formatCurrency(form.price_per_tag)" disabled class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-50" />
                </div>
              </div>
            </div>

            <!-- Dynamic product rows -->
            <div v-if="form.procured_products.length > 0" class="space-y-3 mb-4">
              <div v-for="(item, index) in form.procured_products" :key="index" class="flex items-end gap-3 bg-gray-50 rounded-md p-3">
                <!-- Product name (read-only, set when selected) -->
                <div class="flex-1 min-w-0">
                  <label class="block text-xs text-gray-500 mb-1">{{ $t('framework_agreements.product') }}</label>
                  <input type="text" :value="item.product_name + (item.variant_name ? ' — ' + item.variant_name : '')" disabled class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white" />
                </div>
                <!-- Article number -->
                <div class="w-28">
                  <label class="block text-xs text-gray-500 mb-1">{{ $t('framework_agreements.article_number') }}</label>
                  <input type="text" :value="item.article_number" disabled class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white" />
                </div>
                <!-- Purchase type -->
                <div class="w-32">
                  <label class="block text-xs text-gray-500 mb-1">{{ $t('framework_agreements.purchase_type') }}</label>
                  <select v-model="item.purchase_type" :disabled="isEditing && user.role !== 'admin'" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    <option value="rental">{{ $t('framework_agreements.rental') }}</option>
                    <option value="cash">{{ $t('framework_agreements.cash') }}</option>
                  </select>
                </div>
                <!-- Price -->
                <div class="w-32">
                  <label class="block text-xs text-gray-500 mb-1">{{ $t('framework_agreements.price') }}</label>
                  <input type="number" v-model.number="item.price" :disabled="isEditing && user.role !== 'admin'" step="0.01" min="0" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                </div>
                <!-- Remove button -->
                <div v-if="!isEditing || user.role === 'admin'" class="pb-1">
                  <button type="button" @click="removeProcuredProduct(index)" class="text-red-400 hover:text-red-600 p-1">
                    <BaseIcon icon="times" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Add product -->
            <div v-if="!isEditing || user.role === 'admin'" class="relative">
              <div class="flex items-center gap-2">
                <div class="relative flex-1">
                  <input 
                    v-model="productSearch" 
                    @input="onProductSearchInput"
                    @focus="showProductDropdown = true"
                    @blur="hideProductDropdown"
                    type="text" 
                    :placeholder="$t('framework_agreements.search_product_placeholder')"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                  <div v-if="showProductDropdown && filteredProducts.length > 0" class="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    <template v-for="product in filteredProducts">
                      <!-- Product without variants, or product as main entry -->
                      <div 
                        v-if="!product.hasVariants"
                        :key="product._id"
                        @mousedown.prevent="addProcuredProduct(product, null)"
                        class="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm flex justify-between"
                      >
                        <span>{{ product.display_name || product.name }}</span>
                        <span class="text-gray-400 text-xs">{{ product.articleNumber }} · {{ product.category }}</span>
                      </div>
                      <!-- Product with variants — show each variant -->
                      <template v-if="product.hasVariants && product.variants && product.variants.length > 0">
                        <div :key="product._id + '-header'" class="px-3 py-1 bg-gray-50 text-xs font-medium text-gray-500 border-t">
                          {{ product.display_name || product.name }}
                        </div>
                        <div 
                          v-for="variant in product.variants" 
                          :key="product._id + '-' + variant._id"
                          @mousedown.prevent="addProcuredProduct(product, variant)"
                          class="px-3 py-2 pl-6 hover:bg-gray-100 cursor-pointer text-sm flex justify-between"
                        >
                          <span>{{ variant.name }}</span>
                          <span class="text-gray-400 text-xs">{{ variant.articleNumber || product.articleNumber }} · {{ product.category }}</span>
                        </div>
                      </template>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <p v-if="form.procured_products.length === 0 && !hasLegacyPrices" class="text-xs text-gray-400 mt-2">{{ $t('framework_agreements.no_products_added') }}</p>
          </div>

          <!-- Agreement Expiry & Contract Period -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('framework_agreements.contract_period') }}</label>
              <input type="number" v-model.number="form.contract_period" @change="onContractPeriodChange" :disabled="isEditing && user.role !== 'admin'" min="0" step="1" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Antal månader" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('framework_agreements.expires') }}</label>
              <input type="date" v-model="form.agreement_expires" @change="onAgreementExpiresChange" :disabled="isEditing && user.role !== 'admin'" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
            </div>
          </div>

          <!-- Securdia Guide Prices (dynamic, tied to procured products) -->
          <div class="border-t pt-4">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">{{ $t('framework_agreements.guide_prices') }}</h3>

            <!-- Legacy display for old agreements that used hardcoded guide price fields -->
            <div v-if="isEditing && hasLegacyGuidePrices && form.guide_prices.length === 0" class="mb-4 bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p class="text-xs text-yellow-700 mb-2">{{ $t('framework_agreements.legacy_guide_prices_note') }}</p>
              <div class="grid grid-cols-3 gap-3">
                <div v-if="form.guide_price_alarm"><span class="text-xs text-gray-500">{{ $t('framework_agreements.gp_alarm') }}:</span> <span class="text-sm">{{ formatCurrency(form.guide_price_alarm) }}</span></div>
                <div v-if="form.guide_price_display"><span class="text-xs text-gray-500">{{ $t('framework_agreements.gp_display') }}:</span> <span class="text-sm">{{ formatCurrency(form.guide_price_display) }}</span></div>
                <div v-if="form.guide_price_tag"><span class="text-xs text-gray-500">{{ $t('framework_agreements.gp_tag') }}:</span> <span class="text-sm">{{ formatCurrency(form.guide_price_tag) }}</span></div>
                <div v-if="form.guide_price_alarm_center"><span class="text-xs text-gray-500">{{ $t('framework_agreements.gp_alarm_center') }}:</span> <span class="text-sm">{{ formatCurrency(form.guide_price_alarm_center) }}</span></div>
                <div v-if="form.guide_price_securcloud"><span class="text-xs text-gray-500">{{ $t('framework_agreements.gp_securcloud') }}:</span> <span class="text-sm">{{ formatCurrency(form.guide_price_securcloud) }}</span></div>
                <div v-if="form.guide_price_securpos"><span class="text-xs text-gray-500">{{ $t('framework_agreements.gp_securpos') }}:</span> <span class="text-sm">{{ formatCurrency(form.guide_price_securpos) }}</span></div>
                <div v-if="form.guide_price_recurring_training"><span class="text-xs text-gray-500">{{ $t('framework_agreements.gp_training') }}:</span> <span class="text-sm">{{ formatCurrency(form.guide_price_recurring_training) }}</span></div>
                <div v-if="form.guide_price_sim_card"><span class="text-xs text-gray-500">{{ $t('framework_agreements.gp_sim_card') }}:</span> <span class="text-sm">{{ formatCurrency(form.guide_price_sim_card) }}</span></div>
              </div>
              <div v-if="hasLegacyAccessoryPrices" class="mt-3 pt-2 border-t border-yellow-200">
                <p class="text-xs font-medium text-gray-600 mb-1">{{ $t('framework_agreements.gp_accessories') }}:</p>
                <div class="grid grid-cols-3 gap-3">
                  <div v-if="form.guide_price_accessories.carabiner"><span class="text-xs text-gray-500">{{ $t('framework_agreements.acc_carabiner') }}:</span> <span class="text-sm">{{ formatCurrency(form.guide_price_accessories.carabiner) }}</span></div>
                  <div v-if="form.guide_price_accessories.charging_cable"><span class="text-xs text-gray-500">{{ $t('framework_agreements.acc_charging_cable') }}:</span> <span class="text-sm">{{ formatCurrency(form.guide_price_accessories.charging_cable) }}</span></div>
                  <div v-if="form.guide_price_accessories.adapter"><span class="text-xs text-gray-500">{{ $t('framework_agreements.acc_adapter') }}:</span> <span class="text-sm">{{ formatCurrency(form.guide_price_accessories.adapter) }}</span></div>
                  <div v-if="form.guide_price_accessories.clip"><span class="text-xs text-gray-500">{{ $t('framework_agreements.acc_clip') }}:</span> <span class="text-sm">{{ formatCurrency(form.guide_price_accessories.clip) }}</span></div>
                  <div v-if="form.guide_price_accessories.carry_case"><span class="text-xs text-gray-500">{{ $t('framework_agreements.acc_carry_case') }}:</span> <span class="text-sm">{{ formatCurrency(form.guide_price_accessories.carry_case) }}</span></div>
                  <div v-if="form.guide_price_accessories.charging_package"><span class="text-xs text-gray-500">{{ $t('framework_agreements.acc_charging_package') }}:</span> <span class="text-sm">{{ formatCurrency(form.guide_price_accessories.charging_package) }}</span></div>
                </div>
              </div>
            </div>

            <!-- Dynamic guide price rows (one per procured product) -->
            <div v-if="form.guide_prices.length > 0" class="space-y-3">
              <div v-for="(gp, index) in form.guide_prices" :key="index" class="flex items-end gap-3 bg-gray-50 rounded-md p-3">
                <div class="flex-1 min-w-0">
                  <label class="block text-xs text-gray-500 mb-1">{{ $t('framework_agreements.product') }}</label>
                  <input type="text" :value="gp.product_name + (gp.variant_name ? ' — ' + gp.variant_name : '')" disabled class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white" />
                </div>
                <div class="w-40">
                  <label class="block text-xs text-gray-500 mb-1">{{ $t('framework_agreements.guide_price_label') }}</label>
                  <input type="number" v-model.number="gp.guide_price" :disabled="isEditing && user.role !== 'admin'" step="0.01" min="0" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
            </div>

            <p v-if="form.guide_prices.length === 0 && !hasLegacyGuidePrices" class="text-xs text-gray-400 mt-2">{{ $t('framework_agreements.guide_prices_empty') }}</p>
          </div>

          <!-- Contact Persons -->
          <div class="border-t pt-4">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">{{ $t('framework_agreements.contacts') }}</h3>
            <div class="space-y-4">
              <!-- Customer Contact -->
              <div class="bg-gray-50 p-3 rounded-md">
                <h4 class="text-xs font-medium text-gray-600 mb-2">{{ $t('framework_agreements.customer_contact') }}</h4>
                <div class="grid grid-cols-3 gap-3">
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Namn</label>
                    <input type="text" v-model="form.customer_contact_person" :disabled="isEditing && user.role !== 'admin'" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Telefon</label>
                    <input type="text" v-model="form.customer_contact_phone" :disabled="isEditing && user.role !== 'admin'" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">E-post</label>
                    <input type="email" v-model="form.customer_contact_email" :disabled="isEditing && user.role !== 'admin'" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                </div>
              </div>
              
              <!-- Securdia Contact -->
              <div class="bg-gray-50 p-3 rounded-md">
                <h4 class="text-xs font-medium text-gray-600 mb-2">{{ $t('framework_agreements.securdia_contact') }}</h4>
                <div class="grid grid-cols-3 gap-3">
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Namn</label>
                    <input type="text" v-model="form.securdia_contact_person" :disabled="isEditing && user.role !== 'admin'" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">Telefon</label>
                    <input type="text" v-model="form.securdia_contact_phone" :disabled="isEditing && user.role !== 'admin'" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label class="block text-xs text-gray-500 mb-1">E-post</label>
                    <input type="email" v-model="form.securdia_contact_email" :disabled="isEditing && user.role !== 'admin'" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Business Value -->
          <div class="border-t pt-4">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">{{ $t('framework_agreements.business_value') }}</h3>
            <div class="grid grid-cols-3 gap-4">
              <div>
                <label class="block text-xs text-gray-500 mb-1">{{ $t('framework_agreements.potential_alarms') }}</label>
                <input type="number" v-model.number="form.potential_alarm_count" :disabled="isEditing && user.role !== 'admin'" min="0" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label class="block text-xs text-gray-500 mb-1">{{ $t('framework_agreements.initial_value') }}</label>
                <input type="number" v-model.number="form.initial_business_value" :disabled="isEditing && user.role !== 'admin'" step="0.01" min="0" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label class="block text-xs text-gray-500 mb-1">{{ $t('framework_agreements.potential_value') }}</label>
                <input type="number" v-model.number="form.potential_business_value" :disabled="isEditing && user.role !== 'admin'" step="0.01" min="0" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex justify-end gap-3 pt-4 border-t flex-wrap">
            <button type="button" @click="closeModal" class="sd-btn sd-btn--secondary">
              {{ isEditing && user.role !== 'admin' ? $t('framework_agreements.close') : $t('framework_agreements.cancel') }}
            </button>
            <button v-if="!isEditing || user.role === 'admin'" type="button" class="sd-btn sd-btn--secondary" :disabled="!form.customer_id" @click="openPreview">
              <BaseIcon icon="eye" class="mr-1" />
              {{ $t('framework_agreements.preview_pdf') }}
            </button>
            <button v-if="!isEditing || user.role === 'admin'" type="submit" class="sd-btn sd-btn--primary" :disabled="isSaving">
              <BaseIcon v-if="isSaving" icon="spinner" spin class="mr-1" />
              {{ isEditing ? $t('framework_agreements.save') : $t('framework_agreements.create') }}
            </button>
            <button v-if="!isEditing || user.role === 'admin'" type="button" class="sd-btn sd-btn--primary" :disabled="isSaving || isGeneratingPdf" @click="saveAndCreatePdf">
              <BaseIcon v-if="isGeneratingPdf" icon="spinner" spin class="mr-1" />
              <BaseIcon v-else icon="file-pdf" class="mr-1" />
              {{ $t('framework_agreements.save_and_create_pdf') }}
            </button>
          </div>
        </form>
      </div>
    </modal>

    <!-- Delete Confirmation Modal -->
    <modal :name="'modal-confirm-delete'" height="auto" width="400px">
      <div class="p-6 text-center">
        <BaseIcon icon="exclamation-triangle" class="text-red-500 text-3xl mb-4" />
        <h3 class="text-lg font-semibold text-gray-800 mb-2">{{ $t('framework_agreements.confirm_delete_title') }}</h3>
        <p class="text-sm text-gray-500 mb-6">{{ $t('framework_agreements.confirm_delete_text') }}</p>
        <div class="flex justify-center gap-3">
          <button @click="$modal.hide('modal-confirm-delete')" class="sd-btn sd-btn--secondary">
            {{ $t('framework_agreements.cancel') }}
          </button>
          <button @click="deleteAgreement" class="sd-btn sd-btn--danger">
            {{ $t('framework_agreements.delete') }}
          </button>
        </div>
      </div>
    </modal>

    <!-- PDF Preview Modal -->
    <modal name="modal-framework-agreement-preview" :scrollable="false" :adaptive="true" height="90%" width="92%" @closed="cleanupPreview">
      <div class="flex h-full" style="height:100%;">
        <!-- Left: PDF iframe -->
        <div class="flex-1 bg-gray-100 relative overflow-hidden">
          <div v-if="isPreviewLoading" class="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
            <BaseIcon icon="spinner" spin class="text-4xl text-gray-400" />
          </div>
          <iframe v-if="previewBlobUrl" :src="previewBlobUrl" class="w-full h-full border-0" style="display:block;" />
        </div>
        <!-- Right: editor panel -->
        <div class="bg-white border-l flex flex-col" style="width:360px;min-width:280px;">
          <!-- Header -->
          <div class="flex items-center justify-between px-4 pt-4 pb-2 border-b shrink-0">
            <h3 class="text-sm font-semibold text-gray-800">{{ $t('framework_agreements.preview_pdf') }}</h3>
            <button type="button" @click="closePreviewModal" class="text-gray-400 hover:text-gray-600">
              <BaseIcon icon="times" />
            </button>
          </div>

          <!-- Tabs -->
          <div class="flex border-b shrink-0">
            <button
              type="button"
              @click="previewTab = 'edit'"
              class="flex-1 py-2 text-xs font-medium transition-colors"
              :class="previewTab === 'edit' ? 'border-b-2 border-primary-500 text-primary-600' : 'text-gray-500 hover:text-gray-700'"
            >{{ $t('framework_agreements.preview_tab_edit') }}</button>
            <button
              type="button"
              @click="previewTab = 'custom'"
              class="flex-1 py-2 text-xs font-medium transition-colors"
              :class="previewTab === 'custom' ? 'border-b-2 border-primary-500 text-primary-600' : 'text-gray-500 hover:text-gray-700'"
            >{{ $t('framework_agreements.preview_tab_custom') }}</button>
          </div>

          <!-- Tab: Edit existing agreement text -->
          <div v-if="previewTab === 'edit'" class="flex-1 overflow-y-auto">
            <p class="text-xs text-gray-400 px-4 py-2">{{ $t('framework_agreements.preview_edit_hint') }}</p>
            <div v-for="section in getPdfSections()" :key="section.id" class="border-b last:border-b-0">
              <div class="flex items-center">
                <button
                  type="button"
                  @click="togglePdfSection(section.id)"
                  class="flex-1 flex items-center justify-between px-4 py-2.5 text-xs font-medium hover:bg-gray-50 text-left"
                  :class="isSectionHidden(section.id) ? 'text-gray-400 line-through' : 'text-gray-700'"
                >
                  <span>{{ section.label }}</span>
                  <span class="ml-2 text-gray-400 shrink-0">{{ activePdfSection === section.id ? '▲' : '▼' }}</span>
                </button>
                <button
                  type="button"
                  @click.stop="toggleHiddenSection(section.id)"
                  class="px-2 py-2.5 text-xs shrink-0"
                  :class="isSectionHidden(section.id) ? 'text-green-500 hover:text-green-700' : 'text-red-400 hover:text-red-600'"
                  :title="isSectionHidden(section.id) ? $t('framework_agreements.preview_show_section') : $t('framework_agreements.preview_hide_section')"
                >
                  <BaseIcon :icon="isSectionHidden(section.id) ? 'eye' : 'eye-slash'" />
                </button>
              </div>
              <div v-if="activePdfSection === section.id" class="px-4 pb-3 flex flex-col gap-3 bg-gray-50">
                <div
                  v-for="para in section.paragraphs"
                  :key="para.key"
                  class="flex flex-col gap-1"
                  :class="isParagraphHidden(para.key) ? 'opacity-50' : ''"
                >
                  <div class="flex items-center justify-between">
                    <label
                      class="text-xs"
                      :class="isParagraphHidden(para.key) ? 'text-gray-400 line-through' : 'text-gray-500'"
                    >{{ para.label }}</label>
                    <div class="flex items-center gap-1">
                      <button
                        v-if="!para.isList"
                        type="button"
                        @click="toggleHiddenParagraph(para.key)"
                        class="text-xs shrink-0"
                        :class="isParagraphHidden(para.key) ? 'text-green-500 hover:text-green-700' : 'text-red-400 hover:text-red-600'"
                        :title="isParagraphHidden(para.key) ? $t('framework_agreements.preview_show_section') : $t('framework_agreements.preview_hide_section')"
                      >
                        <BaseIcon :icon="isParagraphHidden(para.key) ? 'eye' : 'eye-slash'" />
                      </button>
                      <button
                        v-if="form.text_overrides[para.key]"
                        type="button"
                        @click="clearPdfOverride(para.key)"
                        class="text-xs text-blue-500 hover:text-blue-700"
                      >{{ $t('framework_agreements.preview_reset') }}</button>
                    </div>
                  </div>
                  <textarea
                    v-if="!isParagraphHidden(para.key)"
                    :value="form.text_overrides[para.key] || ''"
                    @input="setPdfOverride(para.key, $event.target.value)"
                    @change="_renderPreview()"
                    :placeholder="pdfTextDefaults[para.key] || ''"
                    rows="3"
                    class="w-full border rounded px-2 py-1 text-xs resize-y focus:ring-primary-500 focus:border-primary-500"
                    :class="form.text_overrides[para.key] ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-white'"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Tab: Custom appended sections -->
          <div v-if="previewTab === 'custom'" class="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
            <p class="text-xs text-gray-400">{{ $t('framework_agreements.preview_edit_hint') }}</p>
            <div
              v-for="(section, index) in form.custom_sections"
              :key="index"
              class="border border-gray-200 rounded-md p-3 flex flex-col gap-2 bg-gray-50"
            >
              <div class="flex items-center justify-between">
                <span class="text-xs font-medium text-gray-600">{{ $t('framework_agreements.preview_section') }} {{ index + 1 }}</span>
                <button type="button" @click="removeCustomSection(index)" class="text-red-400 hover:text-red-600 text-xs">
                  {{ $t('framework_agreements.preview_remove_section') }}
                </button>
              </div>
              <input
                v-model="section.heading"
                @change="_renderPreview()"
                type="text"
                :placeholder="$t('framework_agreements.preview_section_heading_placeholder')"
                class="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-primary-500 focus:border-primary-500"
              />
              <textarea
                v-model="section.content"
                @change="_renderPreview()"
                rows="5"
                :placeholder="$t('framework_agreements.preview_section_content_placeholder')"
                class="w-full border border-gray-300 rounded-md px-2 py-1 text-sm resize-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <button type="button" @click="addCustomSection" class="w-full sd-btn sd-btn--secondary text-xs py-1.5">
              <BaseIcon icon="plus" class="mr-1" />
              {{ $t('framework_agreements.preview_add_section') }}
            </button>
          </div>

          <!-- Footer actions -->
          <div class="space-y-2 p-4 border-t shrink-0">
            <button type="button" @click="savePreviewPdf" :disabled="isSavingPreview || !form.customer_id" class="w-full sd-btn sd-btn--primary">
              <BaseIcon v-if="isSavingPreview" icon="spinner" spin class="mr-1" />
              <BaseIcon v-else icon="save" class="mr-1" />
              {{ $t('framework_agreements.preview_save_pdf') }}
            </button>
            <button type="button" @click="closePreviewModal" class="w-full sd-btn sd-btn--secondary">
              {{ $t('framework_agreements.preview_close_preview') }}
            </button>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import axios from "@/utils/axios_base";

export default {
  name: "FrameworkAgreements",
  title() {
    return this.$t("page_titles.framework_agreements");
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      isGeneratingPdf: false,
      isPreviewLoading: false,
      isSavingPreview: false,
      previewBlobUrl: null,
      previewDebounceTimer: null,
      previewTab: 'edit',
      activePdfSection: null,
      isEditing: false,
      agreements: [],
      customers: [],
      selectedAgreement: null,
      newFrameworkFile: null,
      newPriceSupplementFile: null,
      searchQuery: "",
      customerSearch: "",
      showCustomerDropdown: false,
      productSearch: "",
      showProductDropdown: false,
      products: [],
      form: this.getEmptyForm(),
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    filteredAgreements() {
      if (!this.searchQuery) {
        return this.agreements;
      }
      const search = this.searchQuery.toLowerCase();
      return this.agreements.filter(agreement => 
        agreement.customer_name && agreement.customer_name.toLowerCase().includes(search)
      );
    },
    filteredCustomers() {
      if (!this.customerSearch) return this.customers;
      const search = this.customerSearch.toLowerCase();
      return this.customers.filter(customer => 
        customer.name && customer.name.toLowerCase().includes(search)
      );
    },
    filteredProducts() {
      if (!this.productSearch) return this.products;
      const search = this.productSearch.toLowerCase();
      return this.products.filter(p => {
        const name = (p.display_name || p.name || '').toLowerCase();
        const artNr = (p.articleNumber || '').toLowerCase();
        return name.includes(search) || artNr.includes(search);
      });
    },
    hasLegacyPrices() {
      return (this.form.price_per_alarm > 0 || this.form.price_per_display > 0 || this.form.price_per_tag > 0);
    },
    hasLegacyGuidePrices() {
      return (this.form.guide_price_alarm > 0 || this.form.guide_price_display > 0 || this.form.guide_price_tag > 0 ||
        this.form.guide_price_alarm_center > 0 || this.form.guide_price_securcloud > 0 || this.form.guide_price_securpos > 0 ||
        this.form.guide_price_recurring_training > 0 || this.form.guide_price_sim_card > 0 || this.hasLegacyAccessoryPrices);
    },
    pdfTextDefaults() {
      const customer = this.customers.find(c => c._id === this.form.customer_id) || null;
      return this.getPdfTextDefaults(this.form, customer);
    },
    hasLegacyAccessoryPrices() {
      const acc = this.form.guide_price_accessories;
      return acc && (acc.carabiner > 0 || acc.charging_cable > 0 || acc.adapter > 0 || acc.clip > 0 || acc.carry_case > 0 || acc.charging_package > 0);
    },
  },
  methods: {
    getEmptyForm() {
      return {
        customer_id: "",
        customer_name: "",
        country: "se",
        agreement_number: "",
        framework_agreement_file: null,
        framework_agreement_filename: null,
        price_supplement_file: null,
        price_supplement_filename: null,
        price_per_alarm: 0,
        price_per_display: 0,
        price_per_tag: 0,
        procured_products: [],
        agreement_expires: this.getDefaultExpiry(60),
        contract_period: 60,
        guide_price_alarm: 0,
        guide_price_display: 0,
        guide_price_tag: 0,
        guide_price_alarm_center: 0,
        guide_price_securcloud: 0,
        guide_price_securpos: 0,
        guide_price_recurring_training: 0,
        guide_price_sim_card: 0,
        guide_price_accessories: {
          carabiner: 0,
          charging_cable: 0,
          adapter: 0,
          clip: 0,
          carry_case: 0,
          charging_package: 0,
          startup_rental: false,
          insurance: false,
        },
        guide_prices: [],
        customer_contact_person: "",
        customer_contact_phone: "",
        customer_contact_email: "",
        securdia_contact_person: "",
        securdia_contact_phone: "",
        securdia_contact_email: "",
        potential_alarm_count: 0,
        initial_business_value: 0,
        potential_business_value: 0,
        special_conditions: '',
        custom_sections: [],
        text_overrides: {},
        hidden_sections: [],
        hidden_paragraphs: [],
      };
    },

    async fetchAgreements() {
      this.isLoading = true;
      try {
        const { data } = await axios.get(`${process.env.VUE_APP_SERVER_URL}/framework-agreements`);
        this.agreements = data.agreements;
      } catch (error) {
        console.error("Error fetching framework agreements:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchCustomers() {
      try {
        const { data } = await axios.get(`${process.env.VUE_APP_SERVER_URL}/customer/`);
        this.customers = data.data || [];
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    },

    onCustomerSearchInput() {
      this.showCustomerDropdown = true;
      if (!this.customerSearch) {
        this.form.customer_id = "";
        this.form.customer_name = "";
      }
    },

    async selectCustomer(customer) {
      this.form.customer_id = customer._id;
      this.form.customer_name = customer.name;
      this.customerSearch = customer.name;
      this.showCustomerDropdown = false;
    },

    hideCustomerDropdown() {
      setTimeout(() => {
        this.showCustomerDropdown = false;
      }, 200);
    },

    async fetchProducts() {
      try {
        const { data } = await axios.get(`${process.env.VUE_APP_SERVER_URL}/products`);
        this.products = data.products || data.data || [];
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    },

    onProductSearchInput() {
      this.showProductDropdown = true;
    },

    hideProductDropdown() {
      setTimeout(() => {
        this.showProductDropdown = false;
      }, 200);
    },

    addProcuredProduct(product, variant) {
      const productName = product.display_name || product.name;
      const variantId = variant ? variant._id : null;
      const variantName = variant ? variant.name : '';

      this.form.procured_products.push({
        product_id: product._id,
        product_name: productName,
        article_number: variant ? (variant.articleNumber || product.articleNumber) : product.articleNumber,
        category: product.category || '',
        purchase_type: 'rental',
        price: 0,
        variant_id: variantId,
        variant_name: variantName,
      });

      // Also add a corresponding guide price entry
      this.form.guide_prices.push({
        product_id: product._id,
        product_name: productName,
        variant_id: variantId,
        variant_name: variantName,
        guide_price: 0,
      });

      this.productSearch = "";
      this.showProductDropdown = false;
    },

    removeProcuredProduct(index) {
      const removed = this.form.procured_products[index];
      this.form.procured_products.splice(index, 1);

      // Remove matching guide price entry
      const gpIndex = this.form.guide_prices.findIndex(gp =>
        gp.product_id === removed.product_id && gp.variant_id === removed.variant_id
      );
      if (gpIndex !== -1) {
        this.form.guide_prices.splice(gpIndex, 1);
      }
    },

    onFrameworkFileChange(event) {
      this.newFrameworkFile = event.target.files[0] || null;
    },

    onPriceSupplementFileChange(event) {
      this.newPriceSupplementFile = event.target.files[0] || null;
    },

    removeFrameworkAgreement() {
      this.form.framework_agreement_file = null;
      this.form.framework_agreement_filename = null;
      this.form._remove_framework_agreement = true;
    },

    removePriceSupplement() {
      this.form.price_supplement_file = null;
      this.form.price_supplement_filename = null;
      this.form._remove_price_supplement = true;
    },

    openCreateModal() {
      this.isEditing = false;
      this.form = this.getEmptyForm();
      this.customerSearch = "";
      this.productSearch = "";
      this.newFrameworkFile = null;
      this.newPriceSupplementFile = null;
      this.$modal.show("modal-framework-agreement");
    },

    async openEditModal(agreement) {
      this.isEditing = true;
      this.selectedAgreement = agreement;
      this.newFrameworkFile = null;
      this.newPriceSupplementFile = null;
      this.customerSearch = agreement.customer_name || "";
      
      this.form = {
        customer_id: agreement.customer_id,
        customer_name: agreement.customer_name,
        country: agreement.country || 'se',
        agreement_number: agreement.agreement_number || '',
        framework_agreement_file: agreement.framework_agreement_file,
        framework_agreement_filename: agreement.framework_agreement_filename,
        price_supplement_file: agreement.price_supplement_file,
        price_supplement_filename: agreement.price_supplement_filename,
        price_per_alarm: agreement.price_per_alarm || 0,
        price_per_display: agreement.price_per_display || 0,
        price_per_tag: agreement.price_per_tag || 0,
        procured_products: (agreement.procured_products || []).map(p => ({ ...p })),
        agreement_expires: agreement.agreement_expires ? agreement.agreement_expires.substring(0, 10) : "",
        contract_period: agreement.contract_period || 0,
        guide_price_alarm: agreement.guide_price_alarm || 0,
        guide_price_display: agreement.guide_price_display || 0,
        guide_price_tag: agreement.guide_price_tag || 0,
        guide_price_alarm_center: agreement.guide_price_alarm_center || 0,
        guide_price_securcloud: agreement.guide_price_securcloud || 0,
        guide_price_securpos: agreement.guide_price_securpos || 0,
        guide_price_recurring_training: agreement.guide_price_recurring_training || 0,
        guide_price_sim_card: agreement.guide_price_sim_card || 0,
        guide_price_accessories: {
          carabiner: (agreement.guide_price_accessories && agreement.guide_price_accessories.carabiner) || 0,
          charging_cable: (agreement.guide_price_accessories && agreement.guide_price_accessories.charging_cable) || 0,
          adapter: (agreement.guide_price_accessories && agreement.guide_price_accessories.adapter) || 0,
          clip: (agreement.guide_price_accessories && agreement.guide_price_accessories.clip) || 0,
          carry_case: (agreement.guide_price_accessories && agreement.guide_price_accessories.carry_case) || 0,
          charging_package: (agreement.guide_price_accessories && agreement.guide_price_accessories.charging_package) || 0,
          startup_rental: (agreement.guide_price_accessories && agreement.guide_price_accessories.startup_rental) || false,
          insurance: (agreement.guide_price_accessories && agreement.guide_price_accessories.insurance) || false,
        },
        guide_prices: (agreement.guide_prices || []).map(gp => ({ ...gp })),
        special_conditions: agreement.special_conditions || '',
        custom_sections: (agreement.custom_sections && agreement.custom_sections.length > 0)
          ? agreement.custom_sections.map(s => ({ ...s }))
          : (agreement.special_conditions ? [{ heading: 'Tillägg och särskilda villkor', content: agreement.special_conditions }] : []),
        text_overrides: agreement.text_overrides ? { ...agreement.text_overrides } : {},
        hidden_sections: (agreement.hidden_sections || []).slice(),
        hidden_paragraphs: (agreement.hidden_paragraphs || []).slice(),
        customer_contact_person: agreement.customer_contact_person || "",
        customer_contact_phone: agreement.customer_contact_phone || "",
        customer_contact_email: agreement.customer_contact_email || "",
        securdia_contact_person: agreement.securdia_contact_person || "",
        securdia_contact_phone: agreement.securdia_contact_phone || "",
        securdia_contact_email: agreement.securdia_contact_email || "",
        potential_alarm_count: agreement.potential_alarm_count || 0,
        initial_business_value: agreement.initial_business_value || 0,
        potential_business_value: agreement.potential_business_value || 0,
      };
      this.$modal.show("modal-framework-agreement");
    },

    async saveAgreement() {
      await this.persistAgreement({ closeAfter: true, refreshAfter: true });
    },

    async saveAndCreatePdf() {
      if (!this.form.customer_id) return;
      const saved = await this.persistAgreement({ closeAfter: false, refreshAfter: false });
      if (!saved || !saved._id) return;
      const customer = this.customers.find(c => c._id === saved.customer_id);

      // Generate PDF, save it locally for the user, and upload it as the
      // agreement's framework_agreement_file so it persists on the record.
      this.isGeneratingPdf = true;
      try {
        const doc = this.generateFrameworkAgreementPDF(saved, customer);
        const filename = `${saved.agreement_number || 'SAMARBETSAVTAL'}.pdf`;
        doc.save(filename);

        const pdfBlob = doc.output('blob');
        const pdfFile = new File([pdfBlob], filename, { type: 'application/pdf' });
        const formData = new FormData();
        formData.append('framework_agreement', pdfFile);
        await axios.put(
          `${process.env.VUE_APP_SERVER_URL}/framework-agreements/${saved._id}`,
          formData
        );
        await this.fetchAgreements();
      } catch (error) {
        console.error('Error generating/uploading framework agreement PDF:', error);
      } finally {
        this.isGeneratingPdf = false;
      }
      this.closeModal();
    },

    /**
     * Persist current form to API. Returns the saved agreement on success,
     * null on failure.
     */
    async persistAgreement({ closeAfter = true, refreshAfter = true } = {}) {
      if (!this.form.customer_id) return null;
      this.isSaving = true;
      try {
        const formData = this.buildAgreementFormData();
        let response;
        if (this.isEditing && this.selectedAgreement) {
          response = await axios.put(
            `${process.env.VUE_APP_SERVER_URL}/framework-agreements/${this.selectedAgreement._id}`,
            formData
          );
        } else {
          response = await axios.post(
            `${process.env.VUE_APP_SERVER_URL}/framework-agreements`,
            formData
          );
        }
        if (closeAfter) this.closeModal();
        if (refreshAfter) await this.fetchAgreements();
        return (response && response.data && response.data.agreement) || null;
      } catch (error) {
        console.error("Error saving framework agreement:", error);
        return null;
      } finally {
        this.isSaving = false;
      }
    },

    buildAgreementFormData() {
      const formData = new FormData();
      formData.append("customer_id", this.form.customer_id);
      formData.append("customer_name", this.form.customer_name);
      formData.append("country", this.form.country || 'se');
      formData.append("price_per_alarm", this.form.price_per_alarm);
      formData.append("price_per_display", this.form.price_per_display);
      formData.append("price_per_tag", this.form.price_per_tag);
      formData.append("procured_products", JSON.stringify(this.form.procured_products));
      if (this.form.agreement_expires) formData.append("agreement_expires", this.form.agreement_expires);
      formData.append("contract_period", this.form.contract_period);
      formData.append("guide_prices", JSON.stringify(this.form.guide_prices));
      // Legacy guide price fields — still sent for backward compatibility with existing agreements
      formData.append("guide_price_alarm", this.form.guide_price_alarm);
      formData.append("guide_price_display", this.form.guide_price_display);
      formData.append("guide_price_tag", this.form.guide_price_tag);
      formData.append("guide_price_alarm_center", this.form.guide_price_alarm_center);
      formData.append("guide_price_securcloud", this.form.guide_price_securcloud);
      formData.append("guide_price_securpos", this.form.guide_price_securpos);
      formData.append("guide_price_recurring_training", this.form.guide_price_recurring_training);
      formData.append("guide_price_sim_card", this.form.guide_price_sim_card);
      formData.append("guide_price_accessories", JSON.stringify(this.form.guide_price_accessories));
      formData.append("customer_contact_person", this.form.customer_contact_person);
      formData.append("customer_contact_phone", this.form.customer_contact_phone);
      formData.append("customer_contact_email", this.form.customer_contact_email);
      formData.append("securdia_contact_person", this.form.securdia_contact_person);
      formData.append("securdia_contact_phone", this.form.securdia_contact_phone);
      formData.append("securdia_contact_email", this.form.securdia_contact_email);
      formData.append("potential_alarm_count", this.form.potential_alarm_count);
      formData.append("initial_business_value", this.form.initial_business_value);
      formData.append("potential_business_value", this.form.potential_business_value);
      formData.append("special_conditions", this.form.special_conditions || '');
      formData.append("custom_sections", JSON.stringify(this.form.custom_sections || []));
      formData.append("text_overrides", JSON.stringify(this.form.text_overrides || {}));
      formData.append("hidden_sections", JSON.stringify(this.form.hidden_sections || []));
      formData.append("hidden_paragraphs", JSON.stringify(this.form.hidden_paragraphs || []));
      if (this.newFrameworkFile) formData.append("framework_agreement", this.newFrameworkFile);
      if (this.newPriceSupplementFile) formData.append("price_supplement", this.newPriceSupplementFile);
      if (this.form._remove_framework_agreement) formData.append("remove_framework_agreement", "true");
      if (this.form._remove_price_supplement) formData.append("remove_price_supplement", "true");
      return formData;
    },

    downloadPdf(agreement, customer) {
      this.isGeneratingPdf = true;
      try {
        const doc = this.generateFrameworkAgreementPDF(agreement, customer);
        doc.save(`${agreement.agreement_number || 'SAMARBETSAVTAL'}.pdf`);
      } catch (error) {
        console.error('Error generating framework agreement PDF:', error);
      } finally {
        this.isGeneratingPdf = false;
      }
    },

    confirmDelete(agreement) {
      this.selectedAgreement = agreement;
      this.$modal.show("modal-confirm-delete");
    },

    async deleteAgreement() {
      if (!this.selectedAgreement) return;
      try {
        await axios.delete(`${process.env.VUE_APP_SERVER_URL}/framework-agreements/${this.selectedAgreement._id}`);
        this.$modal.hide("modal-confirm-delete");
        this.selectedAgreement = null;
        await this.fetchAgreements();
      } catch (error) {
        console.error("Error deleting framework agreement:", error);
      }
    },

    openPreview() {
      if (!this.form.customer_id) return;
      this.isPreviewLoading = true;
      this.$modal.show('modal-framework-agreement-preview');
      this.$nextTick(() => {
        this._renderPreview();
      });
    },

    _renderPreview() {
      try {
        const customer = this.customers.find(c => c._id === this.form.customer_id);
        const agreementData = {
          ...this.form,
          agreement_number: this.form.agreement_number || 'FÖRHANDSGRANSKNING',
        };
        const doc = this.generateFrameworkAgreementPDF(agreementData, customer);
        const blobUrl = doc.output('bloburl');
        if (this.previewBlobUrl) URL.revokeObjectURL(this.previewBlobUrl);
        this.previewBlobUrl = blobUrl;
      } catch (e) {
        console.error('Preview generation failed:', e);
      } finally {
        this.isPreviewLoading = false;
      }
    },

    debouncedRefreshPreview() {
      clearTimeout(this.previewDebounceTimer);
      this.isPreviewLoading = true;
      this.previewDebounceTimer = setTimeout(() => {
        this._renderPreview();
      }, 600);
    },

    async savePreviewPdf() {
      if (!this.form.customer_id) return;
      this.isSavingPreview = true;
      try {
        const saved = await this.persistAgreement({ closeAfter: false, refreshAfter: false });
        if (!saved || !saved._id) return;
        const customer = this.customers.find(c => c._id === saved.customer_id);
        const doc = this.generateFrameworkAgreementPDF(saved, customer);
        const filename = `${saved.agreement_number || 'SAMARBETSAVTAL'}.pdf`;
        const pdfBlob = doc.output('blob');
        const pdfFile = new File([pdfBlob], filename, { type: 'application/pdf' });
        const formData = new FormData();
        formData.append('framework_agreement', pdfFile);
        await axios.put(
          `${process.env.VUE_APP_SERVER_URL}/framework-agreements/${saved._id}`,
          formData
        );
        await this.fetchAgreements();
        this.closePreviewModal();
        this.closeModal();
      } catch (e) {
        console.error('Error saving preview PDF:', e);
      } finally {
        this.isSavingPreview = false;
      }
    },

    togglePdfSection(sectionId) {
      this.activePdfSection = this.activePdfSection === sectionId ? null : sectionId;
    },

    setPdfOverride(key, value) {
      if (value === '') {
        this.$delete(this.form.text_overrides, key);
      } else {
        this.$set(this.form.text_overrides, key, value);
      }
    },

    clearPdfOverride(key) {
      this.$delete(this.form.text_overrides, key);
      this._renderPreview();
    },

    isParagraphHidden(key) {
      return (this.form.hidden_paragraphs || []).includes(key);
    },

    toggleHiddenParagraph(key) {
      const idx = (this.form.hidden_paragraphs || []).indexOf(key);
      if (idx === -1) {
        this.form.hidden_paragraphs.push(key);
      } else {
        this.form.hidden_paragraphs.splice(idx, 1);
      }
      this._renderPreview();
    },

    isSectionHidden(sectionId) {
      return (this.form.hidden_sections || []).includes(sectionId);
    },

    toggleHiddenSection(sectionId) {
      const idx = (this.form.hidden_sections || []).indexOf(sectionId);
      if (idx === -1) {
        this.form.hidden_sections.push(sectionId);
      } else {
        this.form.hidden_sections.splice(idx, 1);
      }
      this._renderPreview();
    },

    addCustomSection() {
      this.form.custom_sections.push({ heading: '', content: '' });
    },

    removeCustomSection(index) {
      this.form.custom_sections.splice(index, 1);
      this._renderPreview();
    },

    cleanupPreview() {
      if (this.previewBlobUrl) {
        URL.revokeObjectURL(this.previewBlobUrl);
        this.previewBlobUrl = null;
      }
    },

    closePreviewModal() {
      this.$modal.hide('modal-framework-agreement-preview');
    },

    closeModal() {
      this.$modal.hide("modal-framework-agreement");
    },

    resetForm() {
      this.form = this.getEmptyForm();
      this.customerSearch = "";
      this.productSearch = "";
      this.newFrameworkFile = null;
      this.newPriceSupplementFile = null;
      this.selectedAgreement = null;
    },

    formatDate(dateStr) {
      if (!dateStr) return "—";
      return new Date(dateStr).toLocaleDateString("sv-SE");
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return "—";
      return Number(value).toLocaleString("sv-SE", { style: "currency", currency: "SEK", minimumFractionDigits: 0 });
    },

    isExpired(dateStr) {
      if (!dateStr) return false;
      return new Date(dateStr) < new Date();
    },

    getDefaultExpiry(months) {
      const d = new Date();
      d.setMonth(d.getMonth() + months + 1, 0); // last day of target month
      return d.toISOString().substring(0, 10);
    },

    onContractPeriodChange() {
      const months = this.form.contract_period;
      if (months > 0) {
        this.form.agreement_expires = this.getDefaultExpiry(months);
      }
    },

    onAgreementExpiresChange() {
      if (!this.form.agreement_expires) return;
      const now = new Date();
      const target = new Date(this.form.agreement_expires);
      const months = (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth());
      this.form.contract_period = Math.max(0, months);
    },
  },

  async created() {
    this.setPageTitle(this.$t("page_titles.framework_agreements"), "framework_agreements");
    await Promise.all([this.fetchAgreements(), this.fetchCustomers(), this.fetchProducts()]);
  },

  watch: {
    "$i18n.locale"() {
      document.title = this.$t("page_titles.framework_agreements");
      this.setPageTitle(this.$t("page_titles.framework_agreements"), "framework_agreements");
    },
  },
};
</script>
